/**
 * URL utilities for internal/external link detection
 */

import { getLegacyRedirect } from './legacy-routes';

export interface URLDetectionConfig {
  rootPath?: string;
  isCustomDomain?: boolean;
}

/**
 * Checks if a URL is a legacy SC4 link
 */
export const isLegacyLink = (url: URL): boolean => {
  const path = url.pathname.split('/');
  return path.pop() === 'sc4' && url.hash.length > 0;
};

/**
 * Checks if a URL is an internal link based on hostname and path
 */
export const isInternalLink = (
  url: URL,
  config: URLDetectionConfig = {}
): boolean => {
  const { rootPath = '/', isCustomDomain = false } = config;

  const sameHostname = url.hostname === window.location.hostname;
  const isStandardInternalLink =
    sameHostname && url.pathname.startsWith(rootPath);
  const isCustomDomainInternalLink = sameHostname && isCustomDomain;

  return (
    isStandardInternalLink || isCustomDomainInternalLink || isLegacyLink(url)
  );
};

/**
 * Checks if a URL string is internal, handling relative URLs and malformed URLs
 */
export const isInternalUrl = (
  href: string | undefined,
  config: URLDetectionConfig = {}
): boolean => {
  if (!href) return false;

  // Handle relative URLs
  if (href.startsWith('/')) return true;

  try {
    const url = new URL(href);
    return isInternalLink(url, config);
  } catch {
    // If URL parsing fails, treat as external
    return false;
  }
};

/**
 * Legacy assistant route patterns that should be converted to modern assistant routes
 * These match the legacy routes in legacy-routes.js that map to /assistant/* paths
 */
const legacyAssistantRoutePatterns = [
  /^\/service\/[^/]+$/, // /service/:id -> /assistant/service/:id
  /^\/service\/[^/]+\/command\/[^/]+$/, // /service/:id/command/:command -> /assistant/service/:id/command/:command
  /^\/todos$/, // /todos -> /assistant/todos
  /^\/todos\/[^/]+$/, // /todos/:id -> /assistant/todos/:id
  /^\/notifications$/, // /notifications -> /assistant/notifications
];

/**
 * Checks if a legacy hash route is an assistant route
 * @param hashRoute - The route from the hash (e.g., "service/4" from "#service/4")
 * @returns true if it's a legacy assistant route
 */
const isLegacyAssistantRoute = (hashRoute: string): boolean => {
  const route = hashRoute.startsWith('/') ? hashRoute : `/${hashRoute}`;
  return legacyAssistantRoutePatterns.some((pattern) => pattern.test(route));
};

/**
 * Converts a legacy SC4 assistant URL to a modern assistant URL
 * @param url - The URL string to check and convert
 * @returns The converted URL if it's a legacy assistant link, or null if no conversion needed
 */
export const convertLegacyAssistantUrl = (url: string): string | null => {
  if (!isInternalUrl(url)) {
    return null;
  }

  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname.split('/');

    // Check if it's a legacy SC4 link (ends with /sc4 and has a hash)
    if (path.pop() === 'sc4' && urlObj.hash.length > 0) {
      const hashRoute = urlObj.hash.replace('#', '');

      // Only process if it's a legacy assistant route
      if (isLegacyAssistantRoute(hashRoute)) {
        // Convert the legacy route to modern route using getLegacyRedirect
        const modernRoute = getLegacyRedirect(urlObj.hash);

        // Reconstruct the full URL with the modern path (removing /sc4)
        const modernPath = urlObj.pathname.replace(/\/sc4$/, '') + modernRoute;
        return `${urlObj.origin}${modernPath}${urlObj.search}`;
      }
    }
  } catch {
    return null;
  }

  return null;
};
